import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import * as adminApi from '../../api/admin'
import Spinner from '../../components/common/Spinner'
import {
  Avatar,
  Badge,
  Button,
  Card,
  EmptyState,
  Kpi,
  fmtNum,
  fmtRelative,
  styles,
  tokens,
  type BadgeTone,
} from './_ui'

const ROLE_TONE: Record<string, BadgeTone> = {
  OWNER: 'green',
  ADMIN: 'blue',
  VIEWER: 'default',
}

export default function AdminUserDetailPage() {
  const { userId = '' } = useParams()
  const navigate = useNavigate()
  const [detail, setDetail] = useState<adminApi.AdminUserDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  useEffect(() => {
    if (!userId) return
    setLoading(true)
    adminApi
      .getAdminUserDetail(userId)
      .then(setDetail)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed'))
      .finally(() => setLoading(false))
  }, [userId])

  const handleDelete = async () => {
    if (!detail) return
    const shopCount = detail.owned_shops.length
    const shopWarning =
      shopCount > 0 ? `\n\nThis will also delete ${shopCount} owned shop(s) and all their data.` : ''
    if (
      !confirm(
        `Permanently delete this user and wipe their Telegram identity (analytics included)?` +
          shopWarning +
          `\n\nThe next login will start fresh as a first login. This cannot be undone.`,
      )
    )
      return
    setDeleting(true)
    setDeleteError(null)
    try {
      await adminApi.deleteAdminUser(userId)
      navigate('/admin/users')
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Failed to delete user')
      setDeleting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    )
  }

  if (error || !detail) {
    return (
      <div style={{ padding: 16 }}>
        <EmptyState emoji="⚠️" title={error || 'User not found'} />
      </div>
    )
  }

  const fullName =
    [detail.first_name, detail.last_name].filter(Boolean).join(' ') ||
    detail.telegram_username ||
    '—'

  return (
    <div style={{ padding: '8px 16px 32px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <button
        onClick={() => navigate(-1)}
        style={{
          background: 'transparent',
          border: 'none',
          color: tokens.text,
          opacity: 0.6,
          fontSize: 13,
          padding: '4px 0',
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        ← Back
      </button>

      {/* Hero */}
      <div
        style={{
          background: tokens.cardBg,
          borderRadius: 20,
          padding: 20,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: -50,
            left: -50,
            width: 180,
            height: 180,
            borderRadius: '50%',
            background: 'radial-gradient(circle, #14b8a630, transparent 70%)',
            pointerEvents: 'none',
          }}
        />
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', position: 'relative' }}>
          <Avatar src={detail.photo_url} name={fullName} size={64} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: tokens.text,
                margin: 0,
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
              }}
            >
              {fullName}
            </h2>
            <p
              style={{
                ...styles.subtle,
                fontSize: 12,
                fontFamily: 'monospace',
                margin: '4px 0 0',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {detail.telegram_username ? `@${detail.telegram_username} · ` : ''}
              {detail.telegram_id}
            </p>
            <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
              {detail.is_premium && <Badge tone="amber">⭐ premium</Badge>}
              {!detail.is_active && <Badge tone="red">inactive</Badge>}
              {detail.is_mock && <Badge tone="purple">mock</Badge>}
              {detail.language_code && <Badge>{detail.language_code}</Badge>}
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 8,
            marginTop: 16,
          }}
        >
          <Kpi label="Joined" value={fmtRelative(detail.created_at)} />
          <Kpi label="Last active" value={fmtRelative(detail.last_active_at)} />
        </div>
      </div>

      {/* Activity (30d) */}
      <Card title="Activity · last 30 days">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 8,
          }}
        >
          <Kpi label="views" value={fmtNum(detail.activity_30d.product_views)} tone="blue" />
          <Kpi label="sessions" value={fmtNum(detail.activity_30d.sessions)} tone="teal" />
          <Kpi label="❤ items" value={fmtNum(detail.activity_30d.product_likes)} tone="red" />
          <Kpi label="❤ shops" value={fmtNum(detail.activity_30d.merchant_likes_total)} tone="purple" />
        </div>
        {!detail.public_user_id && (
          <p style={{ ...styles.subtle, fontSize: 11, marginTop: 10 }}>
            No public-user record matched by telegram id — activity may be incomplete.
          </p>
        )}
      </Card>

      {/* Recently viewed shops */}
      <Card title={`Recently viewed shops · ${detail.recent_shops_viewed.length}`}>
        {detail.recent_shops_viewed.length === 0 ? (
          <EmptyState emoji="👀" title="No shop visits yet" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {detail.recent_shops_viewed.map((s) => (
              <button
                key={s.merchant_id}
                onClick={() => navigate(`/admin/shops/${s.merchant_id}`)}
                style={{
                  ...styles.innerTile,
                  textAlign: 'left',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                }}
              >
                <Avatar name={s.business_name} emoji="🏪" size={36} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p
                    style={{
                      ...styles.text,
                      fontSize: 13,
                      fontWeight: 600,
                      margin: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {s.business_name}
                  </p>
                  <p style={{ ...styles.subtle, fontSize: 11, margin: '2px 0 0' }}>
                    {s.last_viewed_at ? `last ${fmtRelative(s.last_viewed_at)}` : ''}
                    {s.slug && ` · /${s.slug}`}
                  </p>
                </div>
                <Badge tone="blue">{fmtNum(s.view_count)} ×</Badge>
                <span style={{ ...styles.faint, fontSize: 14 }}>→</span>
              </button>
            ))}
          </div>
        )}
      </Card>

      {/* Owned shops */}
      <Card title={`Owned shops · ${detail.owned_shops.length}`}>
        {detail.owned_shops.length === 0 ? (
          <EmptyState emoji="🏪" title="No owned shops" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {detail.owned_shops.map((s) => (
              <button
                key={s.id}
                onClick={() => navigate(`/admin/shops/${s.id}`)}
                style={{
                  ...styles.innerTile,
                  textAlign: 'left',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                }}
              >
                <Avatar name={s.business_name} emoji="🏪" size={36} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ ...styles.text, fontSize: 13, fontWeight: 600, margin: 0 }}>
                    {s.business_name}
                  </p>
                  {s.slug && (
                    <p
                      style={{
                        ...styles.subtle,
                        fontSize: 11,
                        fontFamily: 'monospace',
                        margin: '2px 0 0',
                      }}
                    >
                      /{s.slug}
                    </p>
                  )}
                </div>
                {s.is_verified && <Badge tone="green">✓</Badge>}
                <span style={{ ...styles.faint, fontSize: 14 }}>→</span>
              </button>
            ))}
          </div>
        )}
      </Card>

      {/* Memberships */}
      <Card title={`Member of · ${detail.memberships.length}`}>
        {detail.memberships.length === 0 ? (
          <EmptyState emoji="👥" title="Not a member of any other shop" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {detail.memberships.map((m) => (
              <button
                key={m.id}
                onClick={() => navigate(`/admin/shops/${m.merchant_id}`)}
                style={{
                  ...styles.innerTile,
                  textAlign: 'left',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                }}
              >
                <Avatar name={m.merchant_name || m.merchant_id} emoji="🏪" size={36} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p
                    style={{
                      ...styles.text,
                      fontSize: 13,
                      fontWeight: 600,
                      margin: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {m.merchant_name || m.merchant_id}
                  </p>
                  {m.created_at && (
                    <p style={{ ...styles.subtle, fontSize: 11, margin: '2px 0 0' }}>
                      since {fmtRelative(m.created_at)}
                    </p>
                  )}
                </div>
                <Badge tone={ROLE_TONE[m.role] ?? 'default'}>{m.role}</Badge>
                <span style={{ ...styles.faint, fontSize: 14 }}>→</span>
              </button>
            ))}
          </div>
        )}
      </Card>

      {/* Danger zone */}
      <Card title="Danger zone">
        <p style={{ ...styles.subtle, fontSize: 12, margin: '0 0 12px' }}>
          Permanently delete this user and wipe their Telegram identity — owned shops,
          memberships and analytics included. The next login re-emulates a first login.
        </p>
        {deleteError && (
          <p style={{ color: tokens.red, fontSize: 12, margin: '0 0 10px' }}>{deleteError}</p>
        )}
        <Button variant="danger" onClick={handleDelete} disabled={deleting}>
          {deleting ? 'Deleting…' : 'Delete user'}
        </Button>
      </Card>
    </div>
  )
}
